import { INSTRUMENTS, EASTMONEY_ONLY } from '../lib/instruments';
import { useSelection } from '../lib/selection';
import { api, type Quote } from '../lib/api';
import { useAsync } from '../lib/dataHooks';
import { upDown } from '../lib/chartTheme';
import type { ViewId } from '../lib/app';

/**
 * 指数行情 —— 接入 /api/quote（需求 §F1）
 *
 * 全部指数**合并 1 次请求**（腾讯批量接口），不是逐个请求。
 * 快照 TTL 10s 由服务端缓存，前端不做额外轮询 —— 本地应用无需秒级刷新。
 */
export function MarketView({ onNavigate }: { onNavigate: (v: ViewId) => void }) {
  const { instrument: current, select } = useSelection();

  const q = useAsync(() => api.quotes(INSTRUMENTS.map((i) => i.code)), []);
  const byCode = new Map<string, Quote>((q.data?.items ?? []).map((x) => [x.code, x]));

  const openChart = (code: string) => {
    select(code);
    onNavigate('chart');
  };

  return (
    <div className="flex flex-col p-3">
      <div className="mb-3 flex items-center gap-2">
        <h1 className="text-[14px] font-semibold">指数行情</h1>
        {q.status === 'loading' && (
          <span className="text-[12px]" style={{ color: 'var(--text-3)' }}>加载中…</span>
        )}
        {q.status === 'error' && (
          <span className="text-[12px]" style={{ color: 'var(--warn)' }}>
            ⚠ {q.error}
            <button type="button" onClick={q.reload} className="ml-1.5 underline">重试</button>
          </span>
        )}
        <span className="ml-auto text-[12px]" style={{ color: 'var(--text-3)' }}>
          {q.data ? `${q.data.items.length}/${INSTRUMENTS.length} 可用 · ${q.data.source}` : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {INSTRUMENTS.map((inst) => {
          const s = byCode.get(inst.code);
          const active = inst.code === current.code;
          const degraded = EASTMONEY_ONLY.has(inst.code);

          /* 无快照：区分「仅东财受限」与「通用失败」，提示语不同 */
          if (!s) {
            return (
              <div key={inst.code}
                className="flex flex-col justify-center rounded-[10px] border p-3"
                style={{ background: 'var(--bg-surface)', borderStyle: 'dashed' }}>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[15px] font-semibold">{inst.name}</span>
                  <span className="num text-[11px]" style={{ color: 'var(--text-3)' }}>{inst.code}</span>
                </div>
                <div className="mt-1.5 text-[12px]" style={{ color: 'var(--warn)' }}>
                  ⚠ {degraded ? '数据源受限 · 仅东财支持' : '暂无快照'}
                </div>
                <div className="mt-1 text-[11px]" style={{ color: 'var(--text-3)' }}>
                  {degraded ? '腾讯/新浪均无此代码，东财解封后可重试' : '上游源可能暂不可用'}
                </div>
              </div>
            );
          }

          const dir = upDown(s.pctChange);
          return (
            <button
              key={inst.code}
              type="button"
              onClick={() => openChart(inst.code)}
              aria-current={active ? 'true' : undefined}
              aria-label={`${inst.name} ${inst.code}，${s.pctChange >= 0 ? '涨' : '跌'} ${Math.abs(s.pctChange).toFixed(2)}%，查看图表`}
              className="rounded-[10px] border p-3 text-left transition-colors hover:border-[var(--accent)]"
              style={{
                background: 'var(--bg-surface)',
                boxShadow: 'var(--shadow-card)',
                borderColor: active ? 'var(--accent)' : 'var(--border)',
                borderWidth: active ? 2 : 1,
                padding: active ? 11 : 12,
              }}
            >
              <div className="flex items-baseline gap-1.5">
                <span className="text-[15px] font-semibold">{inst.name}</span>
                <span className="num text-[11px]" style={{ color: 'var(--text-3)' }}>{inst.code}</span>
                {active && (
                  <span className="ml-auto text-[10px]" style={{ color: 'var(--accent)' }}>当前</span>
                )}
              </div>

              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="num text-[24px] font-semibold leading-none">
                  {num(s.price) !== null ? num(s.price)!.toFixed(2) : '—'}
                </span>
                <span className={`num text-[13px] font-semibold ${dir}`}>
                  {s.pctChange > 0 ? '▲' : s.pctChange < 0 ? '▼' : ''}
                  {s.pctChange.toFixed(2)}%
                </span>
              </div>

              {/* 迷你走势：当日高低区间相对位置 */}
              <div className="my-1.5 h-6">
                <svg viewBox="0 0 100 24" preserveAspectRatio="none"
                  className="h-full w-full" aria-hidden>
                  <rect x="0" y="0" width="100" height="24" fill="transparent" />
                  {[0, 1, 2, 3].map((i) => (
                    <line key={i} x1={25 + i * 18} x2={25 + i * 18} y1="0" y2="24"
                      stroke="var(--border)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                  ))}
                  <line x1="0" x2="100" y1={priceToY(s.prevClose, s.low, s.high)}
                    y2={priceToY(s.prevClose, s.low, s.high)}
                    stroke="var(--text-3)" strokeWidth="0.75" strokeDasharray="2 2"
                    vectorEffect="non-scaling-stroke" />
                  <circle cx={priceToX(s.price, s.low, s.high)} cy={priceToY(s.price, s.low, s.high)}
                    r="2.5" fill={s.pctChange >= 0 ? 'var(--up)' : 'var(--down)'} />
                </svg>
              </div>

              <div className="num flex text-[12px]" style={{ color: 'var(--text-2)' }}>
                <span>{s.amount != null ? fmtAmount(s.amount) : '—'}</span>
                <span className="ml-auto">
                  {num(s.high) !== null ? num(s.high)!.toFixed(0) : '—'}/
                  {num(s.low) !== null ? num(s.low)!.toFixed(0) : '—'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 价格 → SVG y（0 顶部 / 24 底部），high==low 时居中避免除零 */
function priceToY(p: number, lo: number, hi: number): number {
  if (hi === lo) return 12;
  return 22 - ((p - lo) / (hi - lo)) * 20;
}
function priceToX(p: number, lo: number, hi: number): number {
  if (hi === lo) return 50;
  return 4 + ((p - lo) / (hi - lo)) * 92;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * 成交额格式化。**入参单位是万元**（腾讯快照 [37] 的实际单位）。
 * 若按「元」处理会差 1e4 倍 —— 实测上证 67939899 万 = 6794 亿，
 * 按元算会显示成 67939899 亿。故此处以万为基准换算。
 *
 * ⚠️ 入参可能是 null（后端对港股等「上游不提供」的字段会省略或给 null）。
 *    此前调用处用 `!== undefined` 守卫，挡不住 null，直接
 *    `null.toFixed(0)` 崩溃（行情页整页白屏）。此处做全量兜底，
 *    让本函数成为**永不抛出**的纯函数。
 */
function fmtAmount(wan: number | null | undefined): string {
  if (typeof wan !== 'number' || !Number.isFinite(wan)) return '—';
  if (wan >= 1e8) return `${(wan / 1e4).toFixed(0)}万亿`;   // 1e8 万 = 1e12 元
  if (wan >= 1e4) return `${(wan / 1e4).toFixed(2)}亿`;     // 1e4 万 = 1e8 元 → 67939899 万 = 6794.00 亿
  return `${wan.toFixed(0)}万`;
}